import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Grid, Header, Divider, Form, Popup, Icon } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { get, has } from 'lodash';
import { submit, formValueSelector } from 'redux-form';

import { TitledCard } from 'Components/';
import { Main } from 'Containers/';
import MPWWebApi from 'API/MPWApi';
import { INTEGRATION_PREVIEW_FORM_NAME } from 'Common/FormEnum';
import { toastError, toastSuccess } from 'Common/ToastHelper';
import { StudentProfileEnum } from 'Common/FieldEnum';
import {
  SISFieldEnum,
  SISStudentPreviewEnum,
} from 'Common/Constants/SISIntegration';
import IntegrationPreviewForm from './IntegrationPreviewForm';
import { setYesNoDialogProps } from 'Components/dialogs/state/actions';
import { WEBSITE_TITLE } from 'Common/Constants';

class IntegrationPreview extends Component {
  static propTypes = {
    integrationName: PropTypes.string.isRequired,
    gradeOptions: PropTypes.array.isRequired,
    siteOptions: PropTypes.array.isRequired,
    languageOptions: PropTypes.array.isRequired,
    nextStep: PropTypes.func.isRequired,
    prevStep: PropTypes.func.isRequired,
    isComplete: PropTypes.bool.isRequired,
    stepComplete: PropTypes.func,
  };

  static defaultProps = {
    stepComplete: () => {},
  };

  state = {
    data: null,
    loading: false,
    isMappedDataCorrect: false,
  };

  getMainProps = () => {
    const { loading, data } = this.state;
    const { isComplete, prevStep, nextStep, submit } = this.props;
    return {
      toolbox: {
        backButtonProps: { shown: true, callback: prevStep },
        rightButtons: [
          {
            content: 'Preview',
            onClick: submit,
            loading,
          },
          {
            content: 'Import',
            onClick: async () => {
              if (await this.isMappedDataCorrect()) {
                this.importStudent();
              }
            },
            disabled: !data,
            loading,
          },
          {
            content: 'Next',
            onClick: async () => {
              if (await this.isMappedDataCorrect()) {
                nextStep();
              }
            },
            disabled: !(data || isComplete),
            loading,
          },
        ],
        shown: true,
      },
    };
  };

  loadPreview = async ({ studentId }) => {
    const { integrationName } = this.props;
    this.setState({ loading: true });

    try {
      const response = await MPWWebApi.getGeneric(
        `sisintegration/${integrationName}/preview/${studentId}`
      );

      if (response.data.Data) {
        this.setState({
          data: this.reducePreviewData(response.data.Data),
          studentId,
        });
      } else {
        toastError('Student not found, check that the student ID is valid.');
      }
    } catch (error) {
      toastError(
        error.response.data.Errors[0].Title ||
          error.response.data.Errors[0].Message
      );
    }

    this.setState({ loading: false });
  };

  importStudent = async () => {
    const { integrationName, studentId } = this.props;
    this.setState({ loading: true });

    try {
      await MPWWebApi.getGeneric(
        `sisintegration/${integrationName}/import/${studentId}`
      );
      toastSuccess(`Student ${studentId} imported.`);
    } catch (error) {
      toastError(
        error.response.data.Errors[0].Title ||
          error.response.data.Errors[0].Message
      );
    }

    this.setState({ loading: false });
  };

  reducePreviewData = data => {
    const { gradeOptions, siteOptions, languageOptions } = this.props;

    const formatters = {
      [StudentProfileEnum.LanguageId]: key => {
        const language = languageOptions.find(l => l.key === data[key]);
        if (language) {
          return { key: SISFieldEnum.Language, value: language.text };
        }
        return getDefault(key);
      },
      Site: key => {
        const site = siteOptions.find(i => i.key === data[key]);
        if (site) {
          return { key, value: site.text };
        }
        return getDefault(key);
      },
      Grade: key => {
        const grade = gradeOptions.find(i => i.key === data[key]);
        if (grade) {
          return { key, value: grade.text };
        }
        return getDefault(key);
      },
    };

    const getDefault = key => ({ key, value: data[key] });

    return Object.keys(data)
      .map(key => get(formatters, key, getDefault)(key))
      .filter(i => i.key !== SISStudentPreviewEnum.PSEnrollStatus);
  };

  isMappedDataCorrect = () => {
    if (this.props.isComplete) {
      return true;
    }

    return new Promise(resolve => {
      this.props.setYesNoDialogProps({
        showDialog: true,
        title: 'Continue',
        question:
          'Have you checked the data in the preview and ensured that it is mapped correctly?',
        yesCallback: () => {
          this.props.stepComplete();
          resolve(true);
        },
        noCallback: () => resolve(false),
        cancelCallback: () => resolve(false),
      });
    });
  };

  getTooltip = key => {
    const tooltips = {
      [SISFieldEnum.Allergies]: `Allergies that do not already exist in ${WEBSITE_TITLE} will not be imported.`,
    };

    return has(tooltips, key) ? (
      <Popup
        basic
        trigger={<Icon name="question circle outline" />}
        content={<div>{get(tooltips, key)}</div>}
      />
    ) : (
      undefined
    );
  };

  render() {
    const { data, loading } = this.state;
    return (
      <React.Fragment>
        <Main {...this.getMainProps()}>
          <Form>
            <TitledCard title={'Preview'}>
              <IntegrationPreviewForm
                onSubmit={this.loadPreview}
                onChange={() => {
                  data && this.setState({ data: null });
                }}
                loading={loading}
              />
              {data && (
                <React.Fragment>
                  <Divider />
                  <Grid>
                    {data.map(item => {
                      return (
                        <Grid.Column width="4" key={item.key}>
                          <Header as="h5">
                            {item.key}
                            {this.getTooltip(item.key)}
                          </Header>
                          {item.value}
                        </Grid.Column>
                      );
                    })}
                    <Grid.Row />
                  </Grid>
                </React.Fragment>
              )}
            </TitledCard>
          </Form>
        </Main>
      </React.Fragment>
    );
  }
}

const mapDispatchToProps = {
  submit: () => submit(INTEGRATION_PREVIEW_FORM_NAME),
  setYesNoDialogProps,
};

const mapStateToProps = state => ({
  studentId: formValueSelector(INTEGRATION_PREVIEW_FORM_NAME)(
    state,
    SISFieldEnum.StudentId
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(IntegrationPreview);



// WEBPACK FOOTER //
// ./src/components/SISIntegrations/IntegrationPreview.js